import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Head, Link, router, useForm, Deferred, usePage } from '@inertiajs/react'
import type { SharedProps } from '@/frontend/types/inertia'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, AlertTriangle } from 'lucide-react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import WorkerPortalShell from '@/frontend/layouts/worker-first/WorkerPortalShell'
import { usePermissions } from '@/frontend/hooks/usePermissions'
import { useOptionalInertiaProps } from '@/frontend/hooks/useOptionalInertiaProps'
import { AvatarGenerator } from '@/frontend/components/avatar-generator'
import WorkerOperationsDashboard from '@/frontend/components/DigitalWorkers/perfil/WorkerOperationsDashboard'
import WorkerChatTab from '@/frontend/components/DigitalWorkers/perfil/WorkerChatTab'
import SubscriptionStatusCard from '@/frontend/components/DigitalWorkers/SubscriptionStatusCard'
import WorkerProfileForm from '@/frontend/components/DigitalWorkers/WorkerProfileForm'
import ChannelsGrid from '@/frontend/components/DigitalWorkers/ChannelsGrid'
import OnboardingLaunchPanel from '@/frontend/components/DigitalWorkers/OnboardingLaunchPanel'
import AdvancedSettings from '@/frontend/components/DigitalWorkers/AdvancedSettings'
import WorkerBrowserSettings from '@/frontend/components/DigitalWorkers/WorkerBrowserSettings'
import WorkerWorkflowPermissions, { type WorkerWorkflowAccess } from '@/frontend/features/Praxis/WorkerWorkflowPermissions'
import WorkerFleetMachineCard from './_WorkerFleetMachineCard'
import type { ApprovalItem } from '@/frontend/components/approvals/ApprovalQueue'
import type { WorkerFeedPayload } from '@/frontend/features/Home/types'
import type { WorkProcess } from '@/frontend/features/HomeWork/types'
import DangerZone from '@/frontend/components/DigitalWorkers/DangerZone'
import AllowlistTab from '@/frontend/components/DigitalWorkers/AllowlistTab'
import RotinasTab from '@/frontend/components/DigitalWorkers/RotinasTab'
import MemoriaTab from '@/frontend/components/DigitalWorkers/MemoriaTab'
import MemoryExplorerSkeleton from '@/frontend/components/DigitalWorkers/memory-bank/MemoryExplorerSkeleton'
import TokenUsageAnalytics from '@/frontend/components/DigitalWorkers/token-usage/TokenUsageAnalytics'
import TokenUsageAnalyticsSkeleton from '@/frontend/components/DigitalWorkers/token-usage/TokenUsageAnalyticsSkeleton'
import type { MemoryBankPayload, MemoryCopy, MemoryDetailPayload } from '@/frontend/components/DigitalWorkers/memory-bank/types'
import WebhooksTab from '@/frontend/components/DigitalWorkers/WebhooksTab'
import EvalsTab from '@/frontend/components/DigitalWorkers/EvalsTab'
import SectionUnavailableState, { type SectionUnavailableReason } from '@/frontend/components/DigitalWorkers/SectionUnavailableState'
import { formatWhatsApp } from '@/frontend/utils/whatsappMask'
import {
  FormCardSkeleton, TableCardSkeleton,
} from '@/frontend/components/skeletons/ContentSkeletons'
import type { DigitalWorkerDetail, BillingPlanOption, OrgIntegration, AvailableIntegration, IntegrationToolkit, GoogleAccountOption, OrganizationContext, AllowlistData, DiscordBot, CronData, RoutineExecution, TokenUsageCopy, TokenUsageReport, EvalRunsData, WorkerStats, WebhookCopy, WebhookData } from './types'
import type { AgentChatDigitalWorker, AgentChatProject, AgentChatSessionDetail, AgentChatSessionSummary, AgentChatWorkspaceProject } from '@/frontend/pages/Dashboard/AgentChats/types'

interface Props {
  page_title: string
  worker: DigitalWorkerDetail
  // Section-scoped (SZ-1562): eager on the "geral" landing only.
  worker_stats?: WorkerStats
  // Fila "Precisa de você" deste agente (SZ-1599/C.4) — landing only.
  pending_approvals?: ApprovalItem[] | null
  // Atividade escopada a este agente para o painel "O que a {Nome} fez"
  // (dashboard de operação, Iteração 3 Onda B) — deferida na landing,
  // ausente nas demais seções.
  worker_feed?: WorkerFeedPayload
  // Processos vinculados ao agente (landing only, Onda B): nome + status
  // reais, sem execução (o vínculo processo↔rotina não existe).
  worker_processes?: WorkProcess[]
  job_titles: string[]
  // Eager in settings (subscription card) and while onboarding.
  billing_plans?: BillingPlanOption[]
  // Onboarding-scoped (SZ-1562): the launch panel is the only consumer, so
  // these arrive eagerly only with ?onboarding=1 (any section) and stay
  // available through partial reloads otherwise.
  all_org_integrations?: OrgIntegration[]
  available_integrations?: AvailableIntegration[]
  integration_toolkits?: IntegrationToolkit[]
  integration_categories?: string[]
  organization_context?: OrganizationContext
  google_ads_accounts?: GoogleAccountOption[]
  google_analytics_properties?: GoogleAccountOption[]
  meta_ad_accounts?: GoogleAccountOption[]
  allowlist: AllowlistData | null
  telegram_allowlist: AllowlistData | null
  discord_bots: DiscordBot[]
  whatsapp_business_available?: boolean
  whatsapp_embedded_signup_ready?: boolean
  whatsapp_facebook_app_id?: string | null
  meta_wa_embedded_signup_config_id?: string | null
  meta_api_version?: string
  current_user_name?: string | null
  current_user_phone?: string | null
  crons?: CronData | null
  // Execuções REAIS das rotinas (Onda C) — deferida com os crons na aba Rotinas.
  routine_executions?: RoutineExecution[]
  webhooks?: WebhookData | null
  webhook_copy: WebhookCopy
  memory_bank?: MemoryBankPayload
  memory_detail?: MemoryDetailPayload
  memory_copy?: MemoryCopy
  usage_report?: TokenUsageReport
  usage_report_copy?: TokenUsageCopy
  eval_runs?: EvalRunsData | null
  onboarding?: boolean
  praxis_workflows?: WorkerWorkflowAccess
  section?: string
  // Locks become states (SZ-1583): sent when this is an instance-gated
  // section and the instance is not ready — the section renders the reason
  // in place and the server skips the section's data fetch.
  section_unavailable?: { reason: SectionUnavailableReason } | null
  // Aba Chat (Iteração 3, B/C): props do chat, presentes só na seção "chat" e
  // só quando a instância está pronta para conversar (o controller pula a
  // busca quando gated; a aba explica o motivo com o worker vivo).
  chat_organization_id?: string
  chat_digital_worker?: AgentChatDigitalWorker | null
  chat_session?: AgentChatSessionDetail | null
  chat_sessions?: AgentChatSessionSummary[]
  chat_projects?: AgentChatProject[]
  chat_workspace_projects?: AgentChatWorkspaceProject[]
}

// `auditoria` is no longer a Show section (C.2, SZ-1597): its route renders
// the org audit page scoped to the agent (Dashboard/AuditLogs/Index). `chat` é
// a aba do portal (Iteração 3, B/C): mesma Show, section=chat.
type TabType = 'geral' | 'chat' | 'uso' | 'rotinas' | 'memoria' | 'webhooks' | 'comunicacao' | 'evals' | 'configuracoes'

function isTabType(value: string | null | undefined): value is TabType {
  return value === 'geral' || value === 'chat' || value === 'uso' || value === 'rotinas' || value === 'memoria' ||
    value === 'webhooks' || value === 'comunicacao' || value === 'evals' ||
    value === 'configuracoes'
}

// Each section is its own route (SZ-1559); "geral" is the worker landing.
export const workerSectionPath = (workerId: string | number, tab: TabType): string =>
  tab === 'geral'
    ? `/dashboard/digital_workers/${workerId}`
    : `/dashboard/digital_workers/${workerId}/${tab}`

// The server sends the authoritative `section` prop; the URL path is the
// fallback (e.g. stale history entries restored without the prop).
function activeSectionFrom(section: string | undefined, url: string): TabType {
  if (isTabType(section)) return section
  const lastSegment = url.split('?')[0].split('/').filter(Boolean).pop()
  if (isTabType(lastSegment)) return lastSegment
  return 'geral'
}

const TAB_LABELS: { key: TabType; label: string }[] = [
  { key: 'geral', label: 'Visão geral' },
  { key: 'chat', label: 'Chat' },
  { key: 'comunicacao', label: 'Comunicação' },
  { key: 'webhooks', label: 'Webhooks' },
  { key: 'uso', label: 'Uso' },
  { key: 'rotinas', label: 'Rotinas' },
  { key: 'memoria', label: 'Memória' },
  { key: 'evals', label: 'Evals' },
  { key: 'configuracoes', label: 'Configurações' },
]

const DigitalWorkerShow = ({
  page_title, worker, worker_stats, pending_approvals = null, worker_feed, worker_processes = [], all_org_integrations = [], available_integrations = [],
  integration_toolkits = [], integration_categories = [],
  organization_context,
  billing_plans = [],
  google_ads_accounts = [], google_analytics_properties = [], meta_ad_accounts = [],
  allowlist = null, telegram_allowlist = null,
  discord_bots = [], whatsapp_business_available = false,
  whatsapp_embedded_signup_ready = false,
  whatsapp_facebook_app_id = null,
  meta_wa_embedded_signup_config_id = null,
  meta_api_version = 'v22.0',
  current_user_name = null, current_user_phone = null,
  crons = null, routine_executions = [], webhooks = null, webhook_copy, memory_bank, memory_detail, memory_copy,
  usage_report, usage_report_copy, eval_runs = null, onboarding = false, praxis_workflows,
  section = undefined, section_unavailable = null,
  chat_organization_id, chat_digital_worker = null, chat_session = null,
  chat_sessions = [], chat_projects = [], chat_workspace_projects,
}: Props) => {
  const { can_manage_resources } = usePermissions()
  const page = usePage<SharedProps & { crons?: CronData | null; webhooks?: WebhookData | null; eval_runs?: EvalRunsData | null }>()
  const { loadProps } = useOptionalInertiaProps()
  const isPlatformAdmin = page.props.auth.user?.is_admin ?? false
  // With the worker-first sidebar on, the sidebar owns section navigation and
  // the in-page tab bar would duplicate it (SZ-1557). Flag-off users keep the
  // tabs — they are their only section navigation until SZ-1563 removes them.
  const workerFirstNavEnabled = page.props.featureFlags?.worker_first_nav_enabled ?? false
  // Fila "Precisa de você" desligada por padrão (Iteração 1, I1.2): o card do
  // perfil segue o mesmo gate da home e do badge — backend intacto.
  const approvalQueueEnabled = page.props.featureFlags?.approval_queue_enabled ?? false
  const activeTab = activeSectionFrom(section, page.url)
  // The onboarding wizard remains above the active section while its query
  // context is preserved. Let that combined page scroll instead of making the
  // wizard and Chat compete for one viewport-height flex slot.
  const fillChatViewport = activeTab === 'chat' && !onboarding
  // Memory also owns its internal scrolling (inspector, table and timeline).
  // Keep the portal shell bounded so the dashboard does not add a second
  // vertical scrollbar around the explorer.
  const fillPortalViewport = (activeTab === 'chat' || activeTab === 'memoria') && !onboarding
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false)
  const [initialModalToOpen, setInitialModalToOpen] = useState<'google_accounts' | 'meta_accounts' | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollAttemptRef = useRef(0)

  const form = useForm({
    name: worker.name,
    contact_name: worker.contact_name || '',
    contact_whatsapp: worker.contact_whatsapp ? formatWhatsApp(worker.contact_whatsapp) : '',
    stationzero_email: worker.stationzero_email,
    instance_name: worker.instance_name,
    api_endpoint_url: worker.api_endpoint_url || '',
    instance_hook_token: '',
  })

  const handleUpdate = () => form.patch(`/dashboard/digital_workers/${worker.id}`, { preserveScroll: true })

  const isReady = worker.provisioning_status === 'ready'
  const chatReady = isReady && worker.probe_connect_ready

  // Instance-gated sections stay navigable (SZ-1583): while the instance is
  // not ready they render an in-context unavailable state instead of data.
  // Visibility follows the live `worker` prop (not the server prop) so the
  // provisioning poll can flip the page to the real content without a full
  // reload; the server prop supplies the specific reason.
  const gatedSection = activeTab === 'rotinas' || activeTab === 'memoria' || activeTab === 'webhooks' || activeTab === 'evals'
  const sectionUnavailableReason: SectionUnavailableReason = section_unavailable?.reason
    ?? (worker.provisioning_status === 'failed' ? 'instancia_indisponivel' : 'provisionando')

  // Section switching is a real Inertia visit to the section route, so the
  // URL, browser history, and server-loaded section data stay in sync.
  // Carry ?onboarding=1 across sections so the post-wizard onboarding panel
  // survives in-page tab clicks.
  const handleTabChange = useCallback((tab: TabType) => {
    const path = workerSectionPath(worker.id, tab)
    router.visit(onboarding ? `${path}?onboarding=1` : path)
  }, [worker.id, onboarding])

  // Poll for provisioning status
  const isProvisioning = !worker.provisioning_stale && (
    (worker.provisioning_status === 'pending' && worker.billing.active) ||
    worker.provisioning_status === 'provisioning' ||
    (worker.whatsapp_registration_status !== null && !['verified', 'failed', 'not_applicable'].includes(worker.whatsapp_registration_status))
  )
  const shouldPollWorker = isProvisioning || Boolean(worker.avatar_generating)

  useEffect(() => {
    if (shouldPollWorker) {
      pollAttemptRef.current = 0
      const poll = () => {
        router.reload({ only: ['worker'] })
        pollAttemptRef.current += 1
        const baseDelay = onboarding ? 2000 : 5000
        const maxDelay = onboarding ? 10000 : 30000
        const delay = Math.min(baseDelay * Math.pow(1.5, pollAttemptRef.current), maxDelay)
        pollRef.current = setTimeout(poll, delay)
      }
      pollRef.current = setTimeout(poll, onboarding ? 2000 : 5000)
    }
    return () => { if (pollRef.current) clearTimeout(pollRef.current) }
  }, [shouldPollWorker, onboarding])

  // Auto-open sub-account modals after OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const openModal = params.get('open_modal')
    if (openModal === 'meta_accounts' || openModal === 'google_accounts') {
      setInitialModalToOpen(openModal)
    }
  }, [])

  const handleCustomizeSave = (imageData: string, mimeType: string) => {
    setCustomizeModalOpen(false)
    const binary = atob(imageData)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const ext = mimeType.includes('png') ? 'png' : 'jpg'
    const file = new File([bytes], `avatar.${ext}`, { type: mimeType })
    router.post(`/dashboard/digital_workers/${worker.id}`, {
      _method: 'patch', avatar: file,
    }, { preserveScroll: true, forceFormData: true })
  }

  const telegramActive = worker.telegram_connection_status === 'active'
  const whatsappSupported = worker.runtime_capabilities.whatsapp_cloud.supported
  const hasChannels = (whatsappSupported && worker.whatsapp_provider !== 'none') ||
    (worker.runtime_capabilities.telegram.supported && telegramActive)
  const whatsappReadyForMessage =
    whatsappSupported &&
    isReady &&
    Boolean(worker.whatsapp_number) &&
    (
      worker.whatsapp_provider === 'personal' ||
      (
        worker.whatsapp_provider === 'business' &&
        worker.whatsapp_registration_status === 'verified' &&
        Boolean(worker.meta_phone_number_id)
      )
    )
  const profileWhatsApp = whatsappReadyForMessage && worker.whatsapp_number
    ? formatWhatsApp(worker.whatsapp_number)
    : ''

  const visibleTabs = TAB_LABELS.filter(({ key }) => {
    if (key === 'rotinas' || key === 'memoria' || key === 'webhooks' || key === 'evals') return isReady
    // Chat gated como as demais seções de instância (a aba explica o motivo
    // in-place quando não pronto — mas o tab bar legado só a mostra pronta).
    if (key === 'chat') return isReady
    if (key === 'comunicacao') return true
    return true
  })

  useEffect(() => {
    if (activeTab === 'rotinas') loadProps(['crons'], isReady)
    if (activeTab === 'webhooks') loadProps(['webhooks'], isReady)
    if (activeTab === 'evals') loadProps(['eval_runs'], isReady)
    // Chat: o controller só serializa props quando o bridge de chat está
    // pronto. Usar apenas provisioning_status causava reloads repetidos quando
    // a instância estava ready mas probe_connect_ready ainda era false.
    if (activeTab === 'chat') loadProps(['chat_organization_id', 'chat_digital_worker', 'chat_session', 'chat_sessions', 'chat_projects', 'chat_workspace_projects'], chatReady)
    if (activeTab === 'comunicacao') loadProps(['allowlist', 'telegram_allowlist'], hasChannels)
  }, [activeTab, chatReady, hasChannels, isReady, loadProps])

  return (
    <>
      <Head title={page_title} />
      {/* Sem px/py próprio (Iteração 3, fix do "pulo" do header): o container
          canônico do portal (WorkerFirstSidebar / DashboardLayout: max-w-5xl
          mx-auto + p-4 md:p-8, SZ-1588) já dá o espaçamento. O `px-4 py-6` extra
          que morava aqui empurrava a FichaHeader ~16px pra direita SÓ nas seções
          da Show (Visão geral/Uso/Rotinas/Configurações), enquanto as páginas
          filtradas (Processos/Publicações/Auditoria/Integrações/Credenciais) e a
          ProcessoDetail renderizam o WorkerPortalShell direto — o header saltava
          ao trocar de aba. Agora todas partem do mesmo container: só `space-y-6`
          para o ritmo vertical do conteúdo pré-moldura (breadcrumb/alerta). */}
      <div className={fillPortalViewport ? 'flex min-h-0 flex-1 flex-col gap-6' : 'space-y-6'}>
        {/* Com a sidebar worker-first, a volta para a equipe mora no menu da
            org (rodapé) — o breadcrumb duplicado sai, como no protótipo. */}
        {!workerFirstNavEnabled && (
          <Link href="/dashboard">
            <Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /> Início</Button>
          </Link>
        )}

        {/* Moldura do portal (WorkerPortalShell): FichaHeader (identidade em
            TODAS as seções — o banner legado WorkerProfileCard fica só no
            onboarding) + WorkerPortalNav (só irmãs profundas do grupo ativo).
            A MESMA moldura envolve as cinco páginas filtradas do portal, para a
            identidade do agente não sumir ao entrar nelas. */}
        <WorkerPortalShell
          worker={worker}
          section={activeTab}
          fillAvailableHeight={fillPortalViewport}
          notice={!can_manage_resources ? (
            <Alert data-testid="worker-permission-alert" variant="warning"><AlertTriangle className="h-4 w-4" /><AlertTitle>Você não tem permissão para gerenciar colaboradores digitais.</AlertTitle><AlertDescription>Você pode visualizar os detalhes, mas não pode modificá-los.</AlertDescription></Alert>
          ) : undefined}
        >

        {onboarding && (
          <OnboardingLaunchPanel
            worker={worker}
            integrations={all_org_integrations}
            availableIntegrations={available_integrations}
            integrationToolkits={integration_toolkits}
            integrationCategories={integration_categories}
            organizationContext={organization_context}
            googleAdsAccounts={google_ads_accounts}
            googleAnalyticsProperties={google_analytics_properties}
            metaAdAccounts={meta_ad_accounts}
            canManage={can_manage_resources}
            currentUserName={current_user_name}
            currentUserPhone={current_user_phone}
            initialModalToOpen={initialModalToOpen}
            showIdentityHero={false}
            billingPlans={billing_plans}
          />
        )}

        {/* Tab navigation — hidden when the worker-first sidebar owns it */}
        {!workerFirstNavEnabled && (
          <div className="flex gap-1 bg-accent rounded-lg p-1 overflow-x-auto" data-testid="worker-section-tabs">
            {visibleTabs.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  activeTab === key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-accent-foreground'
                }`}
                onClick={() => handleTabChange(key)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Tab content */}
        {gatedSection && !isReady && (
          <SectionUnavailableState
            sectionLabel={TAB_LABELS.find(({ key }) => key === activeTab)?.label ?? activeTab}
            workerName={worker.name}
            reason={sectionUnavailableReason}
          />
        )}

        {/* Visão geral = dashboard de operação do agente (Iteração 3, Onda B):
            3 stats, "O que a {Nome} fez" | "Agendado", processos vinculados em
            lista e o composer compacto "Começar uma conversa" no pé. O
            composer-herói da mini-home saiu. */}
        {activeTab === 'geral' && (
          <>
            {worker.runtime_capabilities.praxis_execution_default.supported && (
              <section className="rounded-xl border border-border bg-card p-5">
                <h2 className="text-sm font-semibold">{worker.runtime_capabilities.praxis_execution_default.title}</h2>
                <p role="status" className="mt-2 text-sm text-muted-foreground">{worker.runtime_capabilities.praxis_execution_default.reason}</p>
              </section>
            )}
            <WorkerOperationsDashboard
              worker={worker}
              stats={worker_stats}
              crons={crons}
              workerFeed={worker_feed}
              processes={worker_processes}
              approvalQueueEnabled={approvalQueueEnabled}
              pendingApprovals={pending_approvals ?? []}
            />
          </>
        )}

        {/* Chat (Iteração 3, B/C): dentro da moldura da ficha —
            lista completa das conversas do agente | thread ativo (runtime
            assistant-ui reusado). Self-gated (WorkerChatTab explica o motivo
            com o worker vivo), então fica fora do gatedSection genérico. */}
        {activeTab === 'chat' && (
          <WorkerChatTab
            worker={worker}
            organizationId={chat_organization_id ?? null}
            chatWorker={chat_digital_worker}
            session={chat_session}
            sessions={chat_sessions}
            projects={chat_projects}
            workspaceProjects={chat_workspace_projects ?? []}
            workspaceProjectsLoaded={chat_workspace_projects !== undefined}
            fillAvailableHeight={fillChatViewport}
          />
        )}

        {activeTab === 'configuracoes' && (
          <div className="space-y-6">
            <WorkerProfileForm
              form={form}
              stationzeroEmailAccountStatus={worker.stationzero_email_account_status}
              workerWhatsApp={profileWhatsApp}
              canManage={can_manage_resources}
              onSave={handleUpdate}
              onOpenCustomize={() => setCustomizeModalOpen(true)}
            />
            <AdvancedSettings form={form} worker={worker} canManage={can_manage_resources} />
            {worker.browser_environment && <WorkerBrowserSettings workerId={worker.id} settings={worker.browser_environment} canManage={can_manage_resources} />}
            {praxis_workflows && <WorkerWorkflowPermissions key={JSON.stringify(praxis_workflows.selected)} workerId={worker.id} access={praxis_workflows} canManage={can_manage_resources} />}
            {/* Assinatura mora aqui desde o Ajuste 3 (Iteração 3): billing é
                gestão da conta do agente, não parte da Visão geral de
                operação. Único card de billing do agente. */}
            <SubscriptionStatusCard worker={worker} plans={billing_plans} canManage={can_manage_resources} />
            {isPlatformAdmin && <WorkerFleetMachineCard fleetMachine={worker.fleet_machine ?? null} />}
            {can_manage_resources && <DangerZone workerId={worker.id} instanceName={worker.instance_name} />}
          </div>
        )}

        {activeTab === 'uso' && (
          <Deferred data="usage_report" fallback={<TokenUsageAnalyticsSkeleton />}>
            {usage_report && usage_report_copy ? (
              <TokenUsageAnalytics
                workerId={String(worker.id)}
                report={usage_report}
                copy={usage_report_copy}
                canManage={can_manage_resources}
              />
            ) : <TokenUsageAnalyticsSkeleton />}
          </Deferred>
        )}

        {activeTab === 'rotinas' && isReady && (
          <Deferred data={['crons', 'routine_executions']} fallback={<TableCardSkeleton rows={4} />}>
            <RotinasTab
              crons={crons ?? null}
              routineExecutions={routine_executions}
              basePath={`/dashboard/digital_workers/${worker.id}`}
              canManage={can_manage_resources}
            />
          </Deferred>
        )}

        {activeTab === 'memoria' && isReady && memory_copy && (
          <div className="flex min-h-0 w-full flex-1">
            <Deferred data="memory_bank" fallback={<MemoryExplorerSkeleton label={memory_copy.status.loading} />}>
              <MemoriaTab
                workerId={worker.id}
                memoryBank={memory_bank}
                memoryDetail={memory_detail}
                canManage={can_manage_resources}
                copy={memory_copy}
              />
            </Deferred>
          </div>
        )}

        {activeTab === 'webhooks' && isReady && (
          <Deferred data="webhooks" fallback={<TableCardSkeleton rows={3} />}>
            <WebhooksTab
              data={webhooks ?? null}
              workerId={worker.id}
              canManage={can_manage_resources}
              copy={webhook_copy}
            />
          </Deferred>
        )}

        {activeTab === 'evals' && isReady && (
          <Deferred data="eval_runs" fallback={<TableCardSkeleton rows={3} />}>
            <EvalsTab
              data={eval_runs ?? null}
              workerId={worker.id}
              canManage={can_manage_resources}
            />
          </Deferred>
        )}

        {activeTab === 'comunicacao' && (
          <div className="space-y-6">
            <ChannelsGrid
              worker={worker}
              whatsappBusinessAvailable={whatsapp_business_available}
              whatsappEmbeddedSignup={{
                ready: whatsapp_embedded_signup_ready,
                facebookAppId: whatsapp_facebook_app_id,
                configId: meta_wa_embedded_signup_config_id,
                apiVersion: meta_api_version,
                digitalWorkerId: worker.id,
                returnTo: workerSectionPath(worker.id, 'comunicacao'),
              }}
              discordBots={discord_bots}
              canManage={can_manage_resources}
            />
            {hasChannels && (
              <Deferred data={['allowlist', 'telegram_allowlist']} fallback={<FormCardSkeleton fields={2} />}>
                <AllowlistTab
                  allowlist={allowlist}
                  telegramAllowlist={telegram_allowlist}
                  hasWhatsApp={whatsappSupported && worker.whatsapp_provider !== 'none'}
                  hasTelegram={worker.runtime_capabilities.telegram.supported && telegramActive}
                  basePath={`/dashboard/digital_workers/${worker.id}`}
                  readOnly={!can_manage_resources}
                />
              </Deferred>
            )}
          </div>
        )}

        </WorkerPortalShell>
      </div>

      {/* Avatar Generator Modal */}
      <Dialog open={customizeModalOpen} onOpenChange={(open) => !open && setCustomizeModalOpen(false)}>
        <DialogContent className="max-w-[800px]">
          <DialogHeader><DialogTitle>Personalizar Avatar</DialogTitle></DialogHeader>
          <AvatarGenerator onSave={handleCustomizeSave} compact />
        </DialogContent>
      </Dialog>
    </>
  )
}

DigitalWorkerShow.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default DigitalWorkerShow
